import { HISTORY_ACTION_MODE } from '@candulabs/craft';
import { useMemo } from 'react';

import { CRAFT_ROOT_ID } from 'craft/constants';
import { useEditor, useMultiNodes } from 'craft/hooks';
import { useIsUsingDefaultCssControls, useViewportBreakpoint } from 'craft/state';

type ModalPosition = {
  verticalPosition: string | undefined | null;
  horizontalPosition: string | undefined | null;
};

export const useModalPosition = () => {
  const { query } = useEditor();
  const viewportBreakpoint = useViewportBreakpoint();
  const isUsingDefaultCssControls = useIsUsingDefaultCssControls();
  const {
    props,
    media,
    actions: { setProps, setCustom },
  } = useMultiNodes([CRAFT_ROOT_ID], (node) => ({
    props: node?.data.props || {},
    media: node?.data.custom?.media || {},
  }));

  const nonResponsivePosition = {
    horizontalPosition: props.horizontalPosition ?? 'center',
    verticalPosition: props.verticalPosition ?? 'center',
  };
  const mediaProps = (media.props || {})[viewportBreakpoint] || {};

  const positionProps = useMemo(() => {
    const mediaPosition = {
      horizontalPosition: mediaProps.horizontalPosition,
      verticalPosition: mediaProps.verticalPosition,
    };
    if (isUsingDefaultCssControls) {
      return nonResponsivePosition;
    }
    if (!mediaPosition.verticalPosition) {
      return nonResponsivePosition;
    }
    return mediaPosition;
  }, [isUsingDefaultCssControls, mediaProps, nonResponsivePosition]);

  const position = useMemo(
    () => ({
      horizontal: (positionProps.horizontalPosition as unknown) as 'left' | 'center' | 'right',
      vertical: (positionProps.verticalPosition as unknown) as 'top' | 'center' | 'bottom',
    }),
    [positionProps],
  );

  const positionName = useMemo(() => `${position.vertical}-${position.horizontal}`, [position]);

  const getPositionWithFallbacks = (newPosition: ModalPosition) => {
    return {
      verticalPosition: newPosition.verticalPosition || 'center',
      horizontalPosition: newPosition.horizontalPosition || 'center',
    };
  };

  const setPosition = (
    newPosition: ModalPosition,
    historyMode?: HISTORY_ACTION_MODE,
    throttleNumber?: number,
  ) => {
    if (isUsingDefaultCssControls) {
      setProps(getPositionWithFallbacks(newPosition), historyMode, throttleNumber);
    } else {
      const custom = query.node(CRAFT_ROOT_ID).get().data.custom || {};
      setCustom(
        {
          ...custom,
          media: {
            ...(custom.media || {}),
            props: {
              ...(custom.media?.props || {}),
              [viewportBreakpoint]: {
                ...((custom.media?.props || {})[viewportBreakpoint] ?? {}),
                ...(newPosition ?? {}),
              },
            },
          },
        },
        historyMode,
        throttleNumber,
      );
    }
  };

  return { positionName, position, positionProps, setPosition };
};
